import React, { useState } from "react";
import axios from "axios";
import { S3_URL_PRODUCTS, BASE_URL } from "../utils/constants";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
  Alert,
  Collapse,
  OverlayTrigger,
  Tooltip,
  InputGroup,
} from "react-bootstrap";
import ShippingOne from "./ShippingOne";
import FooterOne from "./FooterOne";
import BottomFooter from "./BottomFooter";
import { trackOrder } from "../services/nonAuthRequets";

const OrderTracking = () => {
  document.title = `Track Order | Bigmall`;
  const [order, setOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Complaint modal & collapse states
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  // Complaint form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [complain, setComplain] = useState("");
  const [complainSuccess, setComplainSuccess] = useState(false);

  // Helper to trim product names
  const trimProductName = (name) => {
    return name && name.length > 50 ? name.substring(0, 50) + "..." : name;
  };

  // Helper to conditionally render a field (for payment summary)
  const renderField = (label, value, prefix = "") => {
    if (!value) return null;
    return (
      <p className="small mb-1">
        <strong>{label}:</strong> {prefix}
        {value}
      </p>
    );
  };

  // Define a fallback for currency (fail safe LKR)
  const currency =
    order && order.currency && order.currency.trim() !== ""
      ? order.currency
      : "LKR";

  const fetchOrderDetails = async () => {
    if (!trackingNumber.trim()) {
      setError("Please enter a valid tracking number.");
      setOrder(null);
      return;
    }
    setIsLoading(true);
    try {
      setError(null);
      const response = await trackOrder(trackingNumber.trim() || null);
      //console.log("Order response:", response);
      setOrder(response);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError(
        err.response?.data?.message || "Order not found or an error occurred."
      );
      setOrder(null);
    } finally {
      setIsLoading(false);
    }
  };

  const submitComplain = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone || !complain.trim()) {
      alert("All fields are required.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/public/complaint`, {
        trackingNumber: order.orderNumber || null,
        email,
        phoneNumber: phone,
        name,
        content: complain,
      });
      if (response.status === 201 || response.status === 200) {
        setComplainSuccess(true);
        alert(
          "Complaint submitted successfully. We will address your issue soon."
        );
        // Optionally update the order to include complaint info
        setOrder({
          ...order,
          complaint: { status: "submitted", content: complain },
        });
        // Reset complaint form fields
        setName("");
        setEmail("");
        setPhone("");
        setComplain("");
      } else {
        alert("Unexpected response from the server. Please try again later.");
      }
    } catch (err) {
      console.error("Error submitting complaint:", err);
      alert(err.response?.data || err.message || "An error occurred.");
    } finally {
      setIsLoading(false);
      setOpenModal(false);
    }
  };

  return (
    <>
      <Container className="mt-24 px-20">
        {/* Title with Tooltip */}
        <Col className="d-flex flex-column align-items-center">
          <Row className="mb-4 justify-content-center w-100">
            <Col
              xs={12}
              md={6}
              className="d-flex align-items-center justify-content-center"
            >
              <h4 className="mb-0 text-main">Track Your Order</h4>
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip id="tooltip-track-order">
                    User must first track the order to submit a complaint.
                  </Tooltip>
                }
              >
                <i
                  className="ph ph-info ms-2"
                  style={{ cursor: "pointer" }}
                ></i>
              </OverlayTrigger>
            </Col>
          </Row>

          {/* Tracking Number Input */}
          <Row className="mb-4 justify-content-center w-100">
            <Col xs={12} md={6}>
              <InputGroup>
                <Form.Control
                  style={{ outline: "none", boxShadow: "none" }}
                  type="text"
                  placeholder="Enter Tracking Number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
                <Button
                  variant="main"
                  onClick={fetchOrderDetails}
                  disabled={isLoading}
                >
                  {isLoading ? "Searching..." : "Search"}
                </Button>
              </InputGroup>
            </Col>
          </Row>
        </Col>

        {/* Error Alert */}

        {error && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}

        {order && (
          <>
            {/* Order Summary, Billing & Shipping */}
            <Row className="mb-4 mt-8">
              {/* Order Summary Card */}
              <Col xs={12} md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="mb-0" style={{ textAlign: "left" }}>
                      Order Summary
                    </h4>
                  </Card.Header>
                  <Card.Body className="small">
                    {order.orderNumber && (
                      <p>
                        <strong>Order #:</strong> {order.orderNumber}
                      </p>
                    )}
                    {order.paymentMethod && (
                      <p>
                        <strong>Payment Method:</strong> {order.paymentMethod}
                      </p>
                    )}
                    {order.status && (
                      <p>
                        <strong>Status:</strong>{" "}
                        <span className="badge bg-secondary">
                          {order.status.toUpperCase()}
                        </span>
                      </p>
                    )}
                    {order.customerNote && (
                      <p>
                        <strong>Customer Note:</strong> {order.customerNote}
                      </p>
                    )}
                    {order.createdAt && (
                      <p>
                        <strong>Created At:</strong>{" "}
                        {new Date(order.createdAt).toLocaleString()}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Billing Address Card */}
              <Col xs={12} md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="mb-0" style={{ textAlign: "left" }}>
                      Billing Address
                    </h4>
                  </Card.Header>
                  <Card.Body className="small">
                    <p className="mb-1">
                      <strong>Name:</strong> {order.billingFirstName}{" "}
                      {order.billingLastName}
                    </p>
                    <p className="mb-1">
                      <strong>Address:</strong> {order.billingAddress1},{" "}
                      {order.billingCity}
                    </p>
                    <p className="mb-1">
                      <strong>State:</strong> {order.billingState}
                    </p>
                    <p className="mb-1">
                      <strong>Post Code:</strong> {order.billingPostcode}
                    </p>
                    <p className="mb-1">
                      <strong>Country:</strong> {order.billingCountry}
                    </p>
                    {order.billingEmail && (
                      <p className="mb-1">
                        <strong>Email:</strong> {order.billingEmail}
                      </p>
                    )}
                    {order.billingPhone && (
                      <p className="mb-1">
                        <strong>Phone:</strong> {order.billingPhone}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Shipping Address Card */}
              <Col xs={12} md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <h4 className="mb-0" style={{ textAlign: "left" }}>
                      Shipping Address
                    </h4>
                  </Card.Header>
                  <Card.Body className="small">
                    <p className="mb-1">
                      <strong>Name:</strong> {order.shippingFirstName}{" "}
                      {order.shippingLastName}
                    </p>
                    <p className="mb-1">
                      <strong>Address:</strong> {order.shippingAddress1},{" "}
                      {order.shippingCity}
                    </p>
                    <p className="mb-1">
                      <strong>State:</strong> {order.shippingState}
                    </p>
                    <p className="mb-1">
                      <strong>Post Code:</strong> {order.shippingPostcode}
                    </p>
                    <p className="mb-1">
                      <strong>Country:</strong> {order.shippingCountry}
                    </p>
                    {order.shippingEmail && (
                      <p className="mb-1">
                        <strong>Email:</strong> {order.shippingEmail}
                      </p>
                    )}
                    {order.shippingPhone && (
                      <p className="mb-1">
                        <strong>Phone:</strong> {order.shippingPhone}
                      </p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Order Items */}
            <Row className="mb-4">
              <Col xs={12}>
                <h4 className="mb-3" style={{ textAlign: "left" }}>
                  Order Items
                </h4>
                {order.products.map((product, idx) => (
                  <Card key={idx} className="mb-3">
                    <Card.Body className="small">
                      <div className="d-flex align-items-center">
                        <img
                          src={`${S3_URL_PRODUCTS}${product.productImages[0]}`}
                          alt={product.productName}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "contain",
                            marginRight: "10px",
                          }}
                        />
                        <div>
                          <p className="col-auto" style={{ width: "150px" }}>
                            <strong>
                              {trimProductName(product.productName)}
                            </strong>
                          </p>
                        </div>
                        <div className="ms-auto">
                          <p>
                            <strong>Qty:</strong> {product.quantity}
                          </p>
                        </div>
                        <div className="ms-auto">
                          <p className="mb-0">
                            <strong>Price:</strong> {currency} {product.price}
                          </p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </Col>
            </Row>

            {/* Payment Summary */}
            <Row className="mb-4">
              <Col xs={12} className="d-flex justify-content-end">
                <div className="text-end">
                  <div className="fw-bold mb-2">Total Summary</div>
                  {renderField(
                    "Total Amount",
                    order.totalAmount,
                    `${currency} `
                  )}
                  {order.orderDiscount
                    ? renderField(
                        "Discount",
                        order.orderDiscount,
                        `${currency} `
                      )
                    : null}
                  {order.orderTax
                    ? renderField("Tax", order.orderTax, `${currency} `)
                    : null}
                  {order.deliveryFee
                    ? renderField(
                        "Delivery Fee",
                        order.deliveryFee,
                        `${currency} `
                      )
                    : null}
                  <hr />
                  <p className="small">
                    <strong>Net Amount:</strong> {currency} {order.netAmount}
                  </p>
                </div>
              </Col>
            </Row>

            {/* Complaint Handling */}
            {order.complaint && (
              <Row className="mb-4">
                <Col xs={12}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setComplaintOpen(!complaintOpen)}
                    aria-controls="complaint-collapse"
                    aria-expanded={complaintOpen}
                  >
                    {complaintOpen ? "Hide Complaint" : "View Complaint"}
                  </Button>
                  <Collapse in={complaintOpen}>
                    <div id="complaint-collapse" className="mt-3">
                      <Card>
                        <Card.Body className="small">
                          <div className="mb-2">
                            <span className="badge bg-secondary">
                              {order.complaint.status.toUpperCase()}
                            </span>
                          </div>
                          <div>{order.complaint.content}</div>
                        </Card.Body>
                      </Card>
                    </div>
                  </Collapse>
                </Col>
              </Row>
            )}

            {/* Submit Complaint Button */}
            <Row className="mb-4">
              <Col xs={12} className="text-center">
                <Button
                  variant="main"
                  size="sm"
                  onClick={() => setOpenModal(true)}
                >
                  Submit a Complaint
                </Button>
              </Col>
            </Row>
          </>
        )}

        {/* Complaint Submission Modal */}
        <Modal show={openModal} onHide={() => setOpenModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Submit a Complaint</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={submitComplain}>
              <Form.Group className="mb-3" controlId="complaintName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="complaintEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="complaintPhone">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="complaintContent">
                <Form.Label>Your Complaint</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={complain}
                  onChange={(e) => setComplain(e.target.value)}
                  required
                />
              </Form.Group>
              <Button variant="main" type="submit" disabled={isLoading}>
                {isLoading ? "Submitting..." : "Submit Complaint"}
              </Button>
            </Form>
            {complainSuccess && (
              <Alert variant="success" className="mt-3">
                Complaint submitted successfully. We will address your issue
                soon.
              </Alert>
            )}
          </Modal.Body>
        </Modal>
      </Container>
      <ShippingOne />
      <FooterOne />
      <BottomFooter />
    </>
  );
};

export default OrderTracking;
